import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  RocketLaunch,
  CameraAlt,
  Visibility,
  Search,
  Timer,
  FolderOpen,
  Chat,
  Computer,
  GroupAdd,
  Check,
  Apple as AppleIcon,
  DesktopWindows as WindowsIcon,
} from '@mui/icons-material';

// Download URLs - R2 public bucket (all files in root)
const DOWNLOAD_URLS = {
  mac: 'https://pub-93034ca048324f38ad50807a7d67c5c6.r2.dev/TrackerV2-1.0.0-arm64.dmg',
  windows: 'https://pub-93034ca048324f38ad50807a7d67c5c6.r2.dev/TrackerV2%20Setup%201.0.0.exe',
};

export const OnboardingModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (!hasSeenOnboarding) {
      setOpen(true);
    }
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
  };

  const steps = [
    {
      label: 'Welcome to TrackerV2!',
      description: `Your work, visible to you and your team. This quick tour will show you the key features. It only takes 2 minutes.`,
      icon: <RocketLaunch sx={{ fontSize: 40, color: '#ffd369' }} />,
    },
    {
      label: 'Your Personal Work Diary',
      description: `TrackerV2 captures screenshots while you work – your personal work diary. AI analyzes and categorizes your activity automatically. Review your day in AI Analytics.`,
      icon: <CameraAlt sx={{ fontSize: 40, color: '#2196f3' }} />,
    },
    {
      label: 'Team Transparency',
      description: `When you track time, your colleagues can see your screenshots. No lengthy status reports – just honest, visual transparency that builds trust.`,
      icon: <Visibility sx={{ fontSize: 40, color: '#4caf50' }} />,
    },
    {
      label: 'Understand Your Teammates',
      description: `View what your colleagues are focusing on right now. Get a quick overview in Team Overview – no more guessing or status meetings.`,
      icon: <Search sx={{ fontSize: 40, color: '#9c27b0' }} />,
    },
    {
      label: 'Track Time Your Way',
      description: `Use the Start/Stop button for real-time tracking, or log time manually in the Timesheet. Both methods sync with your screenshots automatically.`,
      icon: <Timer sx={{ fontSize: 40, color: '#ff9800' }} />,
    },
    {
      label: 'Organize Your Work',
      description: `Create projects and assign time to them. Track budgets, deadlines, and progress in the Projects section.`,
      icon: <FolderOpen sx={{ fontSize: 40, color: '#607d8b' }} />,
    },
    {
      label: 'Chat & Huddle',
      description: `Use Chat for quick messages in channels or DMs. Start a Huddle for live video conversations – perfect for quick syncs with your team.`,
      icon: <Chat sx={{ fontSize: 40, color: '#e91e63' }} />,
    },
    {
      label: 'Desktop App',
      description: `Download the Desktop App for Mac or Windows. A small widget in your menu bar – start tracking with one click. Screenshots sync automatically with the web app.`,
      icon: <Computer sx={{ fontSize: 40, color: '#00bcd4' }} />,
      downloadLinks: true,
    },
    {
      label: 'Invite Your Team',
      description: `Go to Team to invite members. Assign roles (Admin, Member, Viewer) and manage capacity. Collaboration works best with your whole team onboard.`,
      icon: <GroupAdd sx={{ fontSize: 40, color: '#8bc34a' }} />,
    },
  ];

  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <RocketLaunch color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Get Started with TrackerV2
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 6, 
            borderRadius: 3,
            backgroundColor: 'rgba(255, 211, 105, 0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#ffd369',
            }
          }} 
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Step {activeStep + 1} of {steps.length}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel 
                StepIconProps={{
                  icon: index + 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="600">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ mt: 1, flexShrink: 0 }}>
                     {step.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {step.description}
                    </Typography>
                    {(step as any).downloadLinks && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AppleIcon />}
                          onClick={() => window.open(DOWNLOAD_URLS.mac, '_blank')}
                          sx={{ textTransform: 'none' }}
                        >
                          Download for Mac
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<WindowsIcon />}
                          onClick={() => window.open(DOWNLOAD_URLS.windows, '_blank')}
                          sx={{ textTransform: 'none' }}
                        >
                          Download for Windows
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <div>
                    <Button
                      variant="contained"
                      onClick={index === steps.length - 1 ? handleClose : handleNext}
                      sx={{ 
                        mt: 1, 
                        mr: 1,
                        backgroundColor: index === steps.length - 1 ? '#4caf50' : '#ffd369',
                        color: '#000',
                        '&:hover': {
                          backgroundColor: index === steps.length - 1 ? '#43a047' : '#e6be5f',
                        }
                      }}
                      size="small"
                    >
                      {index === steps.length - 1 ? '✓ Start Using TrackerV2' : 'Next →'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                      size="small"
                    >
                      ← Back
                    </Button>
                    {index < steps.length - 1 && (
                      <Button
                        onClick={handleClose}
                        sx={{ mt: 1, color: 'text.secondary' }}
                        size="small"
                      >
                        Skip Tour
                      </Button>
                    )}
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3, textAlign: 'center' }}>
            <Check sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
            <Typography variant="h6" gutterBottom>You're all set!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click "Start Tracking" in the navigation to begin. Customize your workspace in Settings → Personalization.
            </Typography>
            <Button variant="contained" onClick={handleClose} sx={{ mt: 1 }}>
              Let's Go!
            </Button>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};
